/**
 * Debounced function type.
 */
export type DebouncedFunction<T extends (...args: unknown[]) => void> = ((...args: Parameters<T>) => void) & {
  /**
   * Cancels any pending execution.
   */
  cancel: () => void
}

/**
 * Creates a debounced version of the provided function.
 * @param fn - Function to debounce
 * @param waitMs - Delay in milliseconds
 * @returns Debounced function with cancel method
 */
export function debounce<T extends (...args: unknown[]) => void>(fn: T, waitMs = 300): DebouncedFunction<T> {
  /**
   * Pending debounce timer id.
   */
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  /**
   * Clears any pending timeout.
   */
  const cancel = (): void => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  /**
   * Debounced wrapper function.
   */
  const debounced = ((...args: Parameters<T>) => {
    cancel()
    timeoutId = setTimeout(() => {
      fn(...args)
    }, waitMs)
  }) as DebouncedFunction<T>

  debounced.cancel = cancel

  return debounced
}
